import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
    Snowflake,
    Droplet,
    FlaskConical,
    Activity,
    ArrowRight,
    Bell,
    CheckCircle2,
    Radio,
    Layers,
    AlertOctagon,
} from "lucide-react";

// --------- Applications ---------
const APPLICATIONS = [
    {
        id: "ln2",
        name: "Liquid Nitrogen",
        icon: Snowflake,
        metric: "Weight",
        unit: "lb",
        start: 742,
        threshold: 400,
        end: 385,
        assetLabel: "LN₂ Tank · Bay 3",
        alertTitle: "Low LN₂ Level Detected",
        routedTo: "Alert Sent to Facilities",
        direction: "down",
    },
    {
        id: "oils",
        name: "Oils & Lubricants",
        icon: Droplet,
        metric: "Reserve",
        unit: "gal",
        start: 480,
        threshold: 250,
        end: 218,
        assetLabel: "Lubricant Reservoir · Line 2",
        alertTitle: "Lubricant Reserve Below Threshold",
        routedTo: "Alert Sent to Maintenance",
        direction: "down",
    },
    {
        id: "chem",
        name: "Chemicals & Process Fluids",
        icon: FlaskConical,
        metric: "Volume",
        unit: "L",
        start: 1240,
        threshold: 700,
        end: 632,
        assetLabel: "Process Fluid Tank · A-14",
        alertTitle: "Process Fluid Below Threshold",
        routedTo: "Alert Sent to Process Team",
        direction: "down",
    },
    {
        id: "equipment",
        name: "Critical Equipment",
        icon: Activity,
        metric: "Bearing Temp",
        unit: "°F",
        start: 168,
        threshold: 200,
        end: 214,
        assetLabel: "Compressor · Cell 7",
        alertTitle: "Equipment Temperature Rising",
        routedTo: "Alert Sent to Engineering",
        direction: "up",
    },
];

const FEATURES = [
    {
        icon: Radio,
        title: "Real-Time Monitoring",
        desc: "Continuously track critical equipment and material levels.",
    },
    {
        icon: Bell,
        title: "Automatic Alerts",
        desc: "Notify the right people when something requires attention.",
    },
    {
        icon: Layers,
        title: "Custom Thresholds",
        desc: "Configure warning and critical levels around each application.",
    },
    {
        icon: CheckCircle2,
        title: "Facility-Wide Visibility",
        desc: "Monitor multiple assets and locations from one connected platform.",
    },
];

const WITHOUT_STEPS = [
    "Problem occurs",
    "Someone discovers it",
    "Production stops",
    "Emergency response",
];

const WITH_STEPS = [
    "Condition changes",
    "Kairos detects it",
    "Team alerted",
    "Action taken before downtime",
];

const PHASES = ["Monitor", "Detect", "Alert", "Respond"];

// --------- Section ---------
export const DowntimeSection = () => {
    const [activeId, setActiveId] = useState(APPLICATIONS[0].id);
    const active = useMemo(
        () => APPLICATIONS.find((a) => a.id === activeId) ?? APPLICATIONS[0],
        [activeId]
    );

    return (
        <section
            id="downtime"
            data-testid="downtime-section"
            className="relative bg-white py-24 md:py-32 border-b border-slate-200 scroll-mt-24 overflow-hidden"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                {/* Header */}
                <div className="reveal max-w-3xl">
                    <span className="text-xs md:text-sm font-bold uppercase tracking-[0.28em] text-[#F0493E]">
                        Kairos Platform · Downtime Prevention
                    </span>
                    <h2 className="mt-6 font-display font-extrabold text-4xl md:text-6xl leading-[0.98] tracking-tighter text-[#0A1428] text-balance">
                        Stop downtime
                        <br />
                        before it starts.
                    </h2>
                    <p className="mt-6 text-base md:text-lg text-slate-600 leading-relaxed font-medium max-w-2xl">
                        Monitor the equipment, liquids, gases, and materials
                        your operation depends on — and alert your team
                        before a problem becomes downtime.
                    </p>
                </div>

                {/* Application Tabs */}
                <div
                    data-testid="downtime-tabs"
                    className="mt-14 md:mt-16 flex flex-wrap gap-2 border-b border-slate-200"
                >
                    {APPLICATIONS.map((app) => {
                        const isActive = app.id === activeId;
                        return (
                            <button
                                key={app.id}
                                data-testid={`downtime-tab-${app.id}`}
                                onClick={() => setActiveId(app.id)}
                                className={`group inline-flex items-center gap-2.5 px-4 md:px-5 py-3 md:py-3.5 -mb-px border-b-2 transition-colors ${
                                    isActive
                                        ? "border-[#F0493E] text-[#0A1428]"
                                        : "border-transparent text-slate-500 hover:text-[#0A1428]"
                                }`}
                            >
                                <app.icon
                                    size={16}
                                    strokeWidth={1.5}
                                    className={
                                        isActive
                                            ? "text-[#F0493E]"
                                            : "text-slate-400 group-hover:text-slate-600"
                                    }
                                />
                                <span className="font-display font-bold text-[13px] md:text-sm tracking-tight whitespace-nowrap">
                                    {app.name}
                                </span>
                            </button>
                        );
                    })}
                </div>

                {/* Monitor Panel */}
                <MonitorPanel key={active.id} app={active} />

                {/* Features */}
                <div
                    data-testid="downtime-features"
                    className="mt-20 md:mt-24 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-slate-200 border border-slate-200"
                >
                    {FEATURES.map((f) => (
                        <div
                            key={f.title}
                            data-testid={`downtime-feature-${f.title
                                .toLowerCase()
                                .replace(/[^a-z0-9]+/g, "-")}`}
                            className="reveal group bg-white p-6 md:p-8 hover:bg-slate-50 transition-colors"
                        >
                            <div className="h-11 w-11 grid place-items-center border border-[#0A1428] group-hover:border-[#F0493E] transition-colors">
                                <f.icon
                                    size={18}
                                    strokeWidth={1.5}
                                    className="text-[#F0493E]"
                                />
                            </div>
                            <div className="mt-6 font-display font-bold text-base md:text-lg text-[#0A1428] tracking-tight">
                                {f.title}
                            </div>
                            <p className="mt-3 text-sm md:text-[15px] text-slate-600 leading-relaxed font-medium">
                                {f.desc}
                            </p>
                        </div>
                    ))}
                </div>

                {/* Cost of Downtime */}
                <div className="mt-24 md:mt-28 grid grid-cols-12 gap-x-8 gap-y-10 items-start">
                    <div className="col-span-12 lg:col-span-5 reveal">
                        <span className="text-xs md:text-sm font-bold uppercase tracking-[0.28em] text-[#F0493E]">
                            Why It Matters
                        </span>
                        <h3 className="mt-6 font-display font-extrabold text-3xl md:text-5xl leading-[1] tracking-tighter text-[#0A1428] text-balance">
                            Downtime gets
                            <br />
                            expensive fast.
                        </h3>
                        <p className="mt-6 text-base md:text-lg text-slate-600 leading-relaxed font-medium max-w-md">
                            A depleted tank, low fluid level, missing
                            chemical, or overlooked piece of equipment can
                            interrupt an entire operation. Kairos gives teams
                            earlier visibility so they can respond before the
                            problem becomes downtime.
                        </p>
                    </div>

                    <div className="col-span-12 lg:col-span-7 reveal grid grid-cols-1 md:grid-cols-2 gap-4">
                        <ComparisonColumn
                            testid="downtime-without"
                            title="Without Kairos"
                            steps={WITHOUT_STEPS}
                            variant="muted"
                        />
                        <ComparisonColumn
                            testid="downtime-with"
                            title="With Kairos"
                            steps={WITH_STEPS}
                            variant="accent"
                        />
                    </div>
                </div>

                {/* Closer + CTA */}
                <div className="reveal mt-24 md:mt-28 pt-14 border-t border-slate-200 text-center">
                    <div className="text-[10px] font-bold uppercase tracking-[0.32em] text-slate-500">
                        Built for the Moment That Matters
                    </div>
                    <h3 className="mt-6 font-display font-extrabold text-3xl md:text-5xl leading-[1] tracking-tighter text-[#0A1428] text-balance max-w-3xl mx-auto">
                        Know sooner.
                        <br />
                        Act sooner.
                        <br />
                        <span className="text-[#F0493E]">
                            Keep operations moving.
                        </span>
                    </h3>
                    <Link
                        to="/contact?interest=alert-automation"
                        data-testid="downtime-cta"
                        className="btn-primary mt-10 inline-flex items-center justify-center gap-3 px-8 py-4 text-sm font-bold tracking-wide uppercase rounded-sm"
                    >
                        Explore downtime solutions
                        <ArrowRight size={18} strokeWidth={2} />
                    </Link>
                </div>
            </div>
        </section>
    );
};

// --------- Monitor Panel (live-simulation) ---------
const MonitorPanel = ({ app }) => {
    const [phase, setPhase] = useState(0); // 0 monitor, 1 detect, 2 alert, 3 respond
    const [value, setValue] = useState(app.start);
    const [alertVisible, setAlertVisible] = useState(false);
    const [routedVisible, setRoutedVisible] = useState(false);
    const rafRef = useRef(null);
    const loopRef = useRef(null);

    // Simulation loop
    useEffect(() => {
        // reset
        setValue(app.start);
        setPhase(0);
        setAlertVisible(false);
        setRoutedVisible(false);
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
        if (loopRef.current) clearTimeout(loopRef.current);

        const durationMs = 5200; // total sweep
        const t0 = performance.now();

        const tick = (now) => {
            const t = Math.min(1, (now - t0) / durationMs);
            const eased = 1 - Math.pow(1 - t, 2);
            const next = app.start + (app.end - app.start) * eased;
            const rounded = app.unit === "°F" ? Math.round(next) : Math.round(next);
            setValue(rounded);

            const crossed =
                app.direction === "down"
                    ? next <= app.threshold
                    : next >= app.threshold;

            if (phase < 1 && crossed) setPhase(1);
            if (t < 1) {
                rafRef.current = requestAnimationFrame(tick);
            }
        };
        rafRef.current = requestAnimationFrame(tick);

        // Sequence: after sweep, show alert card, then routed card, hold, then loop
        const seq = async () => {
            await wait(durationMs + 250);
            setPhase(2);
            setAlertVisible(true);
            await wait(1200);
            setPhase(3);
            setRoutedVisible(true);
            await wait(4200);
            // loop
            loopRef.current = setTimeout(() => {
                setValue(app.start);
                setPhase(0);
                setAlertVisible(false);
                setRoutedVisible(false);
                run();
            }, 300);
        };

        const run = () => {
            const t0Inner = performance.now();
            const inner = (now) => {
                const t = Math.min(1, (now - t0Inner) / durationMs);
                const eased = 1 - Math.pow(1 - t, 2);
                const next = app.start + (app.end - app.start) * eased;
                setValue(app.unit === "°F" ? Math.round(next) : Math.round(next));
                const crossed =
                    app.direction === "down"
                        ? next <= app.threshold
                        : next >= app.threshold;
                if (crossed) setPhase((p) => (p < 1 ? 1 : p));
                if (t < 1) rafRef.current = requestAnimationFrame(inner);
                else {
                    loopRef.current = setTimeout(async () => {
                        setPhase(2);
                        setAlertVisible(true);
                        await wait(1200);
                        setPhase(3);
                        setRoutedVisible(true);
                        await wait(4200);
                        loopRef.current = setTimeout(() => {
                            setValue(app.start);
                            setPhase(0);
                            setAlertVisible(false);
                            setRoutedVisible(false);
                            run();
                        }, 300);
                    }, 250);
                }
            };
            rafRef.current = requestAnimationFrame(inner);
        };

        seq();

        return () => {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
            if (loopRef.current) clearTimeout(loopRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [app.id]);

    const isBreached =
        app.direction === "down"
            ? value <= app.threshold
            : value >= app.threshold;

    // Level bar fill percent (down = remaining; up = usage of range)
    const fillPct = (() => {
        if (app.direction === "down") {
            return Math.max(6, Math.min(100, (value / app.start) * 100));
        }
        // upward-drifting temp — show as heat gauge relative to threshold
        const cool = app.start;
        const hot = app.threshold + (app.end - app.threshold) * 1.4;
        return Math.max(6, Math.min(100, ((value - cool) / (hot - cool)) * 100));
    })();

    return (
        <div
            data-testid={`downtime-monitor-${app.id}`}
            className="mt-10 md:mt-12 border border-slate-200 bg-white overflow-hidden"
        >
            {/* Panel header */}
            <div className="px-6 md:px-8 py-5 border-b border-slate-200 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div className="flex items-center gap-3">
                    <div className="h-9 w-9 grid place-items-center border border-slate-300">
                        <app.icon size={16} strokeWidth={1.5} className="text-[#F0493E]" />
                    </div>
                    <div>
                        <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-slate-500">
                            Kairos Monitor · Live
                        </div>
                        <div className="mt-0.5 font-display font-bold text-[#0A1428] text-sm md:text-base tracking-tight">
                            {app.assetLabel}
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-2.5">
                    <span
                        className={`relative inline-flex h-2 w-2 ${
                            isBreached ? "" : ""
                        }`}
                    >
                        <span
                            className={`absolute inline-flex h-full w-full rounded-full pulse-ring ${
                                isBreached ? "bg-[#F0493E]" : "bg-emerald-500"
                            }`}
                        />
                        <span
                            className={`relative inline-flex rounded-full h-2 w-2 ${
                                isBreached ? "bg-[#F0493E]" : "bg-emerald-500"
                            }`}
                        />
                    </span>
                    <span
                        data-testid={`downtime-status-${app.id}`}
                        className={`text-[11px] font-bold uppercase tracking-[0.25em] ${
                            isBreached ? "text-[#F0493E]" : "text-emerald-600"
                        }`}
                    >
                        {isBreached ? "LOW" : "NORMAL"}
                    </span>
                </div>
            </div>

            {/* Panel body */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-px bg-slate-200">
                {/* Metric block */}
                <div className="lg:col-span-8 bg-white p-6 md:p-10">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                        <div>
                            <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-slate-500">
                                {app.metric}
                            </div>
                            <div
                                data-testid={`downtime-value-${app.id}`}
                                className={`mt-3 font-display font-extrabold text-4xl md:text-5xl tnum tracking-tighter transition-colors duration-500 ${
                                    isBreached ? "text-[#F0493E]" : "text-[#0A1428]"
                                }`}
                            >
                                {value.toLocaleString()}
                                <span className="ml-1.5 text-lg md:text-xl align-baseline text-slate-500 font-bold tracking-normal">
                                    {app.unit}
                                </span>
                            </div>
                        </div>
                        <div>
                            <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-slate-500">
                                Threshold
                            </div>
                            <div className="mt-3 font-display font-bold text-xl md:text-2xl text-[#0A1428] tnum">
                                {app.threshold.toLocaleString()}
                                <span className="ml-1.5 text-sm text-slate-500 font-bold tracking-normal">
                                    {app.unit}
                                </span>
                            </div>
                        </div>
                        <div>
                            <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-slate-500">
                                Trend
                            </div>
                            <div className="mt-3 font-display font-bold text-xl md:text-2xl text-[#0A1428]">
                                {app.direction === "down" ? "↘ Falling" : "↗ Rising"}
                            </div>
                        </div>
                    </div>

                    {/* Level bar */}
                    <div className="mt-8">
                        <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-[0.25em] text-slate-500 mb-2">
                            <span>Level</span>
                            <span>{Math.round(fillPct)}%</span>
                        </div>
                        <div className="relative h-3 bg-slate-100 border border-slate-200 overflow-hidden">
                            <div
                                className={`absolute inset-y-0 left-0 transition-[width] duration-300 ease-linear ${
                                    isBreached ? "bg-[#F0493E]" : "bg-emerald-500"
                                }`}
                                style={{ width: `${fillPct}%` }}
                            />
                            {/* threshold marker */}
                            <div
                                className="absolute inset-y-0 w-px bg-[#0A1428]"
                                style={{
                                    left:
                                        app.direction === "down"
                                            ? `${(app.threshold / app.start) * 100}%`
                                            : "50%",
                                }}
                            />
                        </div>
                        <div className="mt-2 text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500">
                            Threshold marker
                        </div>
                    </div>

                    {/* Phase progression */}
                    <div className="mt-10 flex items-center gap-2">
                        {PHASES.map((p, i) => (
                            <div
                                key={p}
                                className="flex items-center gap-2 flex-1"
                            >
                                <div
                                    className={`h-2 w-2 rounded-full transition-colors duration-300 ${
                                        phase >= i
                                            ? "bg-[#F0493E]"
                                            : "bg-slate-300"
                                    }`}
                                />
                                <div
                                    className={`text-[10px] font-bold uppercase tracking-[0.22em] transition-colors duration-300 ${
                                        phase >= i
                                            ? "text-[#0A1428]"
                                            : "text-slate-400"
                                    }`}
                                >
                                    {p}
                                </div>
                                {i < PHASES.length - 1 && (
                                    <div
                                        className={`flex-1 h-px transition-colors duration-300 ${
                                            phase > i ? "bg-[#F0493E]" : "bg-slate-200"
                                        }`}
                                    />
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Alert log */}
                <div className="lg:col-span-4 bg-slate-50 p-6 md:p-8">
                    <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-slate-500">
                        Alert Log
                    </div>
                    <div className="mt-4 space-y-3">
                        <AlertItem
                            visible={true}
                            neutral
                            title={`${app.assetLabel} online`}
                            time="00:00"
                        />
                        <AlertItem
                            visible={alertVisible}
                            title={app.alertTitle}
                            time="04:12"
                            icon={AlertOctagon}
                        />
                        <AlertItem
                            visible={routedVisible}
                            title={app.routedTo}
                            time="04:14"
                            icon={Bell}
                            secondary
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

const AlertItem = ({ visible, title, time, icon: Icon, neutral, secondary }) => (
    <div
        className={`flex items-start gap-3 p-3 border transition-all duration-500 ${
            visible
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-1 pointer-events-none"
        } ${
            neutral
                ? "bg-white border-slate-200"
                : secondary
                ? "bg-white border-[#0A1428]"
                : "bg-[#F0493E]/5 border-[#F0493E]/50"
        }`}
    >
        {Icon && (
            <Icon
                size={14}
                strokeWidth={1.75}
                className={neutral ? "text-slate-400" : "text-[#F0493E] shrink-0 mt-0.5"}
            />
        )}
        {!Icon && (
            <div
                className={`h-2 w-2 rounded-full mt-1.5 shrink-0 ${
                    neutral ? "bg-slate-400" : "bg-[#F0493E]"
                }`}
            />
        )}
        <div className="flex-1">
            <div
                className={`text-[12px] font-bold leading-snug ${
                    neutral ? "text-slate-600" : "text-[#0A1428]"
                }`}
            >
                {title}
            </div>
            <div className="mt-0.5 text-[10px] font-bold uppercase tracking-[0.22em] text-slate-400">
                {time}
            </div>
        </div>
    </div>
);

const ComparisonColumn = ({ title, steps, variant, testid }) => {
    const isAccent = variant === "accent";
    return (
        <div
            data-testid={testid}
            className={`border p-6 md:p-8 ${
                isAccent
                    ? "bg-[#0A1428] border-[#0A1428] text-white"
                    : "bg-slate-50 border-slate-200 text-slate-700"
            }`}
        >
            <div
                className={`text-[10px] font-bold uppercase tracking-[0.32em] ${
                    isAccent ? "text-[#F0493E]" : "text-slate-500"
                }`}
            >
                {title}
            </div>
            <ol className="mt-6 space-y-4">
                {steps.map((s, i) => (
                    <li key={s} className="flex items-start gap-3">
                        <span
                            className={`shrink-0 h-6 w-6 grid place-items-center text-[10px] font-bold tracking-tight ${
                                isAccent
                                    ? "bg-[#F0493E] text-white"
                                    : "bg-white border border-slate-300 text-slate-500"
                            }`}
                        >
                            {i + 1}
                        </span>
                        <span
                            className={`font-display font-bold text-base md:text-lg leading-snug tracking-tight ${
                                isAccent ? "text-white" : "text-[#0A1428]"
                            }`}
                        >
                            {s}
                        </span>
                    </li>
                ))}
            </ol>
        </div>
    );
};

const wait = (ms) => new Promise((r) => setTimeout(r, ms));
